/**
 * BulkBucketDialog — v3.18.4
 *
 * Dialog that runs the AI classifier on every Unassigned requirement,
 * shows the suggestions as an editable table, and applies user's confirmed
 * picks via parallel PATCH /requirements/{id}/hierarchy calls.
 *
 * Triggered from `SelfScoreRollupCard`'s "Bulk-bucket Unassigned with AI"
 * button. Mirrors the Section M extractor's confirm-list pattern.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Sparkles, Loader2, Save, RotateCcw, Check, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BulkBucketDialog({ open, onOpenChange, pursuitId, onApplied }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState([]);
  const [knownTAs, setKnownTAs] = useState([]);
  const [error, setError] = useState(null);

  const fetchPreview = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/ai/requirements/${pursuitId}/classify-task-areas/preview`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setKnownTAs(body.existing_task_areas || []);
      // Seed editable rows: include all by default unless the LLM had no clue
      setRows(
        (body.suggestions || []).map((s) => ({
          requirement_id: s.requirement_id,
          requirement_text: s.requirement_text || '',
          section_reference: s.section_reference || '',
          // editable values (start as the AI suggestion)
          task_area: s.suggested_task_area || '',
          sub_area: s.suggested_sub_area || '',
          requirement_group: s.suggested_requirement_group || '',
          ai_task_area: s.suggested_task_area || '',
          include: !!s.suggested_task_area, // skip rows the LLM left blank by default
        })),
      );
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => {
    if (open) fetchPreview();
  }, [open, fetchPreview]);

  const updateRow = (rid, patch) => {
    setRows((prev) => prev.map((r) => (r.requirement_id === rid ? { ...r, ...patch } : r)));
  };

  const resetRow = (rid) => {
    setRows((prev) =>
      prev.map((r) =>
        r.requirement_id === rid
          ? { ...r, task_area: r.ai_task_area, sub_area: '', include: !!r.ai_task_area }
          : r,
      ),
    );
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => r.include && (r.task_area || '').trim()),
    [rows],
  );

  const toggleAll = (value) => {
    setRows((prev) =>
      prev.map((r) => ({ ...r, include: value && !!(r.task_area || '').trim() })),
    );
  };

  const handleApply = async () => {
    if (selectedRows.length === 0) return;
    setApplying(true);
    let okCount = 0;
    let errCount = 0;
    try {
      // Parallel PATCH per row — reuses the existing hierarchy endpoint
      await Promise.all(
        selectedRows.map(async (r) => {
          try {
            const res = await fetch(
              `${API_URL}/api/ai/requirements/${r.requirement_id}/hierarchy`,
              {
                method: 'PATCH',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  task_area: r.task_area.trim(),
                  sub_area: (r.sub_area || '').trim(),
                  requirement_group: (r.requirement_group || '').trim() || undefined,
                }),
              },
            );
            if (res.ok) {
              okCount += 1;
            } else {
              errCount += 1;
            }
          } catch (_) {
            errCount += 1;
          }
        }),
      );

      if (okCount > 0) {
        toast.success(
          `Applied ${okCount} Task Area assignment${okCount === 1 ? '' : 's'}` +
            (errCount ? ` · ${errCount} failed` : ''),
        );
        if (typeof onApplied === 'function') onApplied(okCount);
        onOpenChange(false);
      } else {
        toast.error('Could not apply any changes — please retry.');
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] flex flex-col"
        data-testid="bulk-bucket-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            Bulk-bucket Unassigned Requirements with AI
          </DialogTitle>
          <DialogDescription>
            The AI Shredder proposed a Task Area for every Unassigned requirement. Review, edit
            if needed, then apply selected rows. Existing labels on this pursuit are reused when
            they fit.
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="space-y-2 py-4" data-testid="bulk-bucket-loading">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Running AI classifier on Unassigned requirements…
              </div>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <div
              className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800"
              data-testid="bulk-bucket-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : rows.length === 0 ? (
            <div
              className="text-center py-8 text-sm text-slate-600"
              data-testid="bulk-bucket-empty"
            >
              No Unassigned requirements found. Everything's already tagged. 🎉
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-3 py-2 px-1 text-xs text-slate-600">
                <span>
                  <strong className="text-slate-900">{rows.length}</strong> suggestions ·{' '}
                  <strong className="text-emerald-700">{selectedRows.length}</strong> selected
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => toggleAll(true)}
                    data-testid="bulk-bucket-select-all"
                  >
                    <Check className="w-3 h-3 mr-1" /> Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => toggleAll(false)}
                    data-testid="bulk-bucket-deselect-all"
                  >
                    <X className="w-3 h-3 mr-1" /> Deselect all
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="p-2 w-10"></th>
                      <th className="p-2">Requirement</th>
                      <th className="p-2 w-48">Task Area</th>
                      <th className="p-2 w-40">Sub-Area</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const aiBlank = !r.ai_task_area;
                      return (
                        <tr
                          key={r.requirement_id}
                          className={cn(
                            'border-b border-slate-100 align-top',
                            r.include ? 'bg-white' : 'bg-slate-50/40',
                          )}
                          data-testid={`bulk-bucket-row-${idx}`}
                        >
                          <td className="p-2 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={r.include}
                              onChange={(e) =>
                                updateRow(r.requirement_id, { include: e.target.checked })
                              }
                              disabled={!(r.task_area || '').trim()}
                              data-testid={`bulk-bucket-row-include-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <p className="text-xs text-slate-800 leading-snug line-clamp-3">
                              {r.requirement_text}
                            </p>
                            {r.section_reference && (
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                §{r.section_reference}
                              </p>
                            )}
                          </td>
                          <td className="p-2">
                            <Input
                              value={r.task_area}
                              onChange={(e) =>
                                updateRow(r.requirement_id, { task_area: e.target.value })
                              }
                              placeholder={aiBlank ? 'AI had no suggestion — type one' : ''}
                              list={`bulk-bucket-ta-list`}
                              className={cn(
                                'h-8 text-xs',
                                aiBlank && 'border-amber-300 bg-amber-50',
                              )}
                              data-testid={`bulk-bucket-row-task-area-${idx}`}
                            />
                            {!aiBlank && r.task_area !== r.ai_task_area && (
                              <p className="text-[10px] text-slate-400 mt-0.5 italic">
                                AI suggested: {r.ai_task_area}
                              </p>
                            )}
                          </td>
                          <td className="p-2">
                            <Input
                              value={r.sub_area}
                              onChange={(e) =>
                                updateRow(r.requirement_id, { sub_area: e.target.value })
                              }
                              placeholder="Optional"
                              className="h-8 text-xs"
                              data-testid={`bulk-bucket-row-sub-area-${idx}`}
                            />
                          </td>
                          <td className="p-2 text-center">
                            {!aiBlank && r.task_area !== r.ai_task_area && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-slate-400 hover:text-slate-700"
                                onClick={() => resetRow(r.requirement_id)}
                                title="Reset to AI suggestion"
                                data-testid={`bulk-bucket-row-reset-${idx}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Datalist for Task Area autocomplete using existing pursuit TAs */}
                <datalist id="bulk-bucket-ta-list">
                  {knownTAs.map((ta) => (
                    <option key={ta} value={ta} />
                  ))}
                </datalist>
              </div>
              {knownTAs.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-2 italic">
                  Tip: existing pursuit labels {knownTAs.slice(0, 5).map((t, i) => (
                    <span key={t}>
                      {i > 0 && ', '}
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 mx-0.5">{t}</Badge>
                    </span>
                  ))} {knownTAs.length > 5 ? `+${knownTAs.length - 5} more ` : ''}are available in the dropdown — reuse them where possible for tidier rollups.
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            data-testid="bulk-bucket-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={applying || loading || selectedRows.length === 0}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="bulk-bucket-apply-btn"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Applying {selectedRows.length}…
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Apply {selectedRows.length} change{selectedRows.length === 1 ? '' : 's'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
